import { Header, Icon, Message, Tooltip } from '@mairistem/composants';
import { ListeTable } from '@mairistem/liste';

import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { ETATS_FACTURE } from '@/constants';
import { RegieContext } from '@/contexts';
import type { FactureListeItemType } from '@/types/facture';
import { formatMonetaire } from '@/utils';

import { SquareTagContainer } from '../Autre';
import EtatFactureSquareTags from './EtatFactureSquareTags';

interface FacturesListeTableRowGlobaleProps {
  item: FactureListeItemType;
  rowProps: Record<string, unknown>;
  /** Télécharge le PDF de la facture à partir de l'identifiant de son document */
  onOpenPdf: (identifiantDocument: number) => void;
  onEncaissement: (id: number) => void;
}

const FacturesListeTableRowGlobale = (props: FacturesListeTableRowGlobaleProps) => {
  const { item, rowProps, onOpenPdf, onEncaissement } = props;

  const { regies, selectedRegie } = React.useContext(RegieContext);
  const regie = _.find(regies, { identifiant: selectedRegie });
  // Si 1 seul logiciel facturier, on masque la colonne
  const hideSourceColumn = _.size(regie?.logicielsFacturier) <= 1;

  const {
    identifiant,
    numero,
    nomPayeur,
    cycleFacturation,
    source,
    dateFacture,
    dateFactureProlongee,
    dateEmission,
    adresseFacturation,
    etat,
    restePayer,
    montantFacture,
    recus,
    prelevee,
    dateRejet,
    dateValidationPosteComptable,
    dateDerniereRelance,
    identifiantDocument,
  } = item;

  const handleDoubleClick = React.useCallback(() => {
    onEncaissement(identifiant);
  }, [identifiant, onEncaissement]);

  const montantEncaisse = React.useMemo(
    () => montantFacture - restePayer,
    [montantFacture, restePayer],
  );

  const numerosRecus = React.useMemo(
    () =>
      _.join(
        _.map(
          recus,
          ({ numero: numeroRecu, dateAnnulation }) => numeroRecu + (dateAnnulation ? ' (A)' : ''),
        ),
        ' / ',
      ),
    [recus],
  );

  const dateDernierEncaissement = React.useMemo(
    () => _.last(_.filter(recus, (recu) => _.isNil(recu.dateAnnulation)))?.dateCreation,
    [recus],
  );

  const dateLimiteEffective = dateFactureProlongee || dateFacture;
  const joursRestants = dayjs(dateLimiteEffective).diff(dayjs(), 'day');
  const estDans7DerniersJours = joursRestants >= 0 && joursRestants < 7;
  const dateAffichee = dayjs(dateLimiteEffective).format('DD/MM/YYYY');

  // Logique des flags selon les règles métier
  const renderSquareTags = React.useMemo(
    () => (
      <EtatFactureSquareTags
        etat={etat}
        regie={regie}
        dateFacture={dateFacture}
        dateFactureProlongee={dateFactureProlongee}
        prelevee={prelevee}
        dateValidationPosteComptable={dateValidationPosteComptable}
        dateRejet={dateRejet}
        dateDernierEncaissement={dateDernierEncaissement}
        dateDerniereRelance={dateDerniereRelance}
      />
    ),
    [
      etat,
      regie,
      dateFacture,
      dateFactureProlongee,
      prelevee,
      dateValidationPosteComptable,
      dateRejet,
      dateDernierEncaissement,
      dateDerniereRelance,
    ],
  );

  return (
    <ListeTable.Row key={identifiant} {...rowProps}>
      <ListeTable.Cell
        name="numero"
        // @ts-expect-error
        title={numero}
        style={{ width: 150 }}
        onDoubleClick={handleDoubleClick}
      >
        <div style={{ display: 'flex' }}>
          <div className="textEllipsis" style={{ paddingRight: 10, flex: 1 }}>
            {numero}
          </div>
          {!_.isNil(identifiantDocument) && (
            <Tooltip
              mouseEnterDelay={100}
              // @ts-expect-error
              flowing
              position="bottom center"
              trigger={
                <Icon
                  data-testid="button-pdf"
                  name="file pdf"
                  style={{ float: 'right' }}
                  fitted
                  onClick={() => onOpenPdf(identifiantDocument)}
                  // Le double-clic sur l'icône ne doit pas déclencher l'encaissement
                  onDoubleClick={(e) => e.stopPropagation()}
                  link
                  size="large"
                  title=""
                />
              }
              message={<>Accéder à la facture PDF</>}
            />
          )}
        </div>
      </ListeTable.Cell>
      <ListeTable.Cell
        name="nomPayeur"
        // @ts-expect-error
        title={nomPayeur}
        onDoubleClick={handleDoubleClick}
      >
        <div style={{ display: 'flex' }}>
          <div className="textEllipsis" style={{ paddingRight: 10, flex: 1 }}>
            {nomPayeur}
          </div>
        </div>
      </ListeTable.Cell>
      <ListeTable.Cell
        name="adresseFacturation"
        // @ts-expect-error
        title={adresseFacturation}
        onDoubleClick={handleDoubleClick}
      >
        {adresseFacturation}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="cycleFacturation"
        // @ts-expect-error
        title={`${cycleFacturation} - ${dayjs(dateEmission).format('DD/MM/YYYY')}`}
        onDoubleClick={handleDoubleClick}
        style={{ position: 'relative' }}
      >
        <div style={{ display: 'flex' }}>
          <div className="textEllipsis" style={{ paddingRight: 10, flex: 1 }}>
            {cycleFacturation}
          </div>
          <span className="hint">{dayjs(dateEmission).format('DD/MM/YYYY')}</span>
        </div>
      </ListeTable.Cell>
      {!hideSourceColumn && (
        <ListeTable.Cell
          name="source"
          textAlign="center"
          style={{ width: 90 }}
          // @ts-expect-error
          onDoubleClick={handleDoubleClick}
        >
          {source}
        </ListeTable.Cell>
      )}
      <ListeTable.Cell
        name="dateFacture"
        textAlign="center"
        style={{ width: 120 }}
        // @ts-expect-error
        onDoubleClick={handleDoubleClick}
        {...(dateFactureProlongee && {
          title: `Date limite non prolongée : ${dayjs(dateFacture).format('DD/MM/YYYY')}`,
        })}
      >
        {estDans7DerniersJours ? (
          <Header as="h5" color="orange" style={{ fontWeight: 600 }}>
            {dateAffichee}
          </Header>
        ) : (
          dateAffichee
        )}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="montant"
        textAlign="right"
        style={{ width: 130 }}
        // @ts-expect-error
        onDoubleClick={handleDoubleClick}
      >
        {formatMonetaire(montantFacture)}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="montantEncaisse"
        textAlign="right"
        style={{ width: 130 }}
        // @ts-expect-error
        onDoubleClick={handleDoubleClick}
      >
        {montantEncaisse > 0 ? formatMonetaire(montantEncaisse) : ''}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="etat"
        stretched
        style={{ width: 200 }}
        // @ts-expect-error
        onDoubleClick={handleDoubleClick}
      >
        <Message
          {...(etat !== ETATS_FACTURE.A_ENCAISSER && {
            color:
              etat === ETATS_FACTURE.ENCAISSEE
                ? 'green'
                : etat === ETATS_FACTURE.ENCAISSEMENT_PARTIEL
                  ? 'yellow'
                  : etat === ETATS_FACTURE.IMPAYE_PARTIEL
                    ? 'orange'
                    : 'red',
          })}
          attached
          style={{
            display: 'flex',
            height: '100%',
            width: '100%',
            margin: 0,
            border: 'none',
            boxShadow: 'none',
            borderRadius: 0,
            padding: 0,
            fontWeight: 500,
            ...(etat === ETATS_FACTURE.A_ENCAISSER ? { backgroundColor: 'transparent' } : {}),
          }}
        >
          <span
            style={{
              flex: 1,
              overflowX: 'hidden',
              textOverflow: 'ellipsis',
              padding: '1.25em 1.5em',
            }}
          >
            {etat}
          </span>
          {React.Children.count(renderSquareTags) > 0 && (
            <SquareTagContainer>{renderSquareTags}</SquareTagContainer>
          )}
        </Message>
      </ListeTable.Cell>
      <ListeTable.Cell
        name="numerosRecus"
        // @ts-expect-error
        title={numerosRecus}
        onDoubleClick={handleDoubleClick}
        style={{ width: 150 }}
      >
        <div className="textEllipsis" style={{ paddingRight: 0, flex: 1 }}>
          {recus.map(({ numero: numeroRecu, dateAnnulation }, index) => (
            <>
              {index > 0 && index < _.size(recus) && ' / '}
              <span
                {...(!_.isNil(dateAnnulation) && {
                  style: { color: 'red', fontStyle: 'italic' },
                  title: `Ce reçu a été annulé le ${dayjs(dateAnnulation).format('DD/MM/YYYY')}`,
                })}
              >
                {numeroRecu}
              </span>
            </>
          ))}
        </div>
      </ListeTable.Cell>
    </ListeTable.Row>
  );
};

export default FacturesListeTableRowGlobale;
